/**
 * Counters with − and + (passengers, pets, bikes), plus optional on/off tiles (assistance). The values
 * live in the screen's draft until a main-bar action sends them; the backend's values are the start.
 *
 * Two looks, same props:
 *  - standard  one row per counter, a plain list;
 *  - cards     the old kiosk's card: groups side by side (`row`) or as centred tiles (`tiles`).
 */
import { z } from 'zod';
import { defineWidget, type WidgetProps } from '../contract';
import { intentSchema, run } from '../intent';
import { Icon } from '../../ui-kit/Icon';
import { Panel } from '../../ui-kit/Panel';
import css from '../Widgets.module.css';
import cards from './CountersCards.module.css';

const itemSchema = z.object({
  id: z.string(),
  label: z.string(),
  sub: z.string().optional(),
  icon: z.string().optional(),
  min: z.number().int().default(0),
  max: z.number().int().default(9),
  value: z.number().int(),
  /** The group it belongs to (cards look); none = the first group. */
  group: z.string().optional(),
});

const schema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  icon: z.string().optional(),
  items: z.array(itemSchema).min(1),
  groups: z.array(z.object({
    id: z.string(),
    title: z.string().optional(),
    icon: z.string().optional(),
    style: z.enum(['row', 'tiles']).default('row'),
  })).default([]),
  /** On/off tiles that do something at once (an intent), shown after the counters. */
  toggles: z.array(z.object({ id: z.string(), label: z.string(), icon: z.string(), selected: z.boolean().default(false), intent: intentSchema })).default([]),
  togglesTitle: z.string().optional(),
  togglesIcon: z.string().optional(),
  footer: z.string().optional(),
});

type Props = z.infer<typeof schema>;
type Item = z.infer<typeof itemSchema>;

function useCounter(item: Item, { ctx, api }: Pick<WidgetProps<Props>, 'ctx' | 'api'>) {
  const drafted = ctx.draft[item.id];
  const value = typeof drafted === 'number' ? drafted : item.value;
  const set = (v: number) => api.setDraft({ [item.id]: Math.max(item.min, Math.min(item.max, v)) });
  return { value, minus: () => set(value - 1), plus: () => set(value + 1) };
}

function Stepper({ item, ctx, api, size = 'md' }: { item: Item; size?: 'md' | 'sm' } & Pick<WidgetProps<Props>, 'ctx' | 'api'>) {
  const { value, minus, plus } = useCounter(item, { ctx, api });
  return (
    <span className={(size === 'sm' ? cards : css).stepper}>
      <button className={size === 'sm' ? cards.minus : css.step} onClick={minus} disabled={value <= item.min} aria-label={`− ${item.label}`}>
        <Icon name="minus" size={size === 'sm' ? 28 : 36} />
      </button>
      <span className={size === 'sm' ? cards.value : css.counterValue} aria-live="polite">{value}</span>
      <button className={size === 'sm' ? cards.plus : css.step} onClick={plus} disabled={value >= item.max} aria-label={`+ ${item.label}`}>
        <Icon name="plus" size={size === 'sm' ? 28 : 36} />
      </button>
    </span>
  );
}

/** Standard: one row per counter. */
function CountersList({ props, ctx, api }: WidgetProps<Props>) {
  return (
    <Panel title={props.title} subtitle={props.subtitle} icon={props.icon} footer={props.footer}>
      <div className={css.counters}>
        {props.items.map((item) => (
          <div key={item.id} className={css.counter}>
            {item.icon && <Icon name={item.icon} size={44} />}
            <div className={css.counterText}>
              <strong>{item.label}</strong>
              {item.sub && <span>{item.sub}</span>}
            </div>
            <Stepper item={item} ctx={ctx} api={api} />
          </div>
        ))}
      </div>
      {props.toggles.length > 0 && (
        <div className={css.examples}>
          {props.togglesTitle && <span className={css.examplesTitle}>{props.togglesTitle}</span>}
          {props.toggles.map((tg) => (
            <button key={tg.id} className={`${css.example} ${tg.selected ? css.selected : ''}`} aria-pressed={tg.selected} onClick={() => run(api, tg.intent)}>
              <Icon name={tg.icon} size={22} />{tg.label}
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

/** Cards: the old kiosk's passenger card — groups side by side or as tiles, toggles as tiles. */
function CountersCards({ props, ctx, api }: WidgetProps<Props>) {
  const groups = props.groups.length > 0 ? props.groups : [{ id: '', style: 'row' as const }];
  const itemsOf = (groupId: string, index: number) =>
    props.items.filter((i) => (i.group ?? groups[0]!.id) === groupId || (index === 0 && !groups.some((g) => g.id === i.group)));
  return (
    <Panel title={props.title} subtitle={props.subtitle} icon={props.icon} footer={props.footer}>
      <div className={cards.card}>
        {groups.map((g, gi) => {
          const items = itemsOf(g.id, gi);
          if (items.length === 0) return null;
          return (
            <section key={g.id || gi} className={cards.group}>
              {g.title && <h3 className={cards.groupTitle}>{g.icon && <Icon name={g.icon} size={20} />}{g.title}</h3>}
              {g.style === 'row' ? (
                <div className={cards.rowGrid}>
                  {items.map((item) => (
                    <div key={item.id} className={cards.rowItem}>
                      <span className={cards.rowText}><strong>{item.label}</strong>{item.sub && <small>{item.sub}</small>}</span>
                      <Stepper item={item} ctx={ctx} api={api} size="sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={cards.tileGrid} style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 3)}, 1fr)` }}>
                  {items.map((item) => (
                    <div key={item.id} className={cards.tile}>
                      {item.icon && <Icon name={item.icon} size={34} className={cards.tileIcon} />}
                      <strong>{item.label}</strong>
                      <Stepper item={item} ctx={ctx} api={api} size="sm" />
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
        {props.toggles.length > 0 && (
          <section className={cards.group}>
            {props.togglesTitle && <h3 className={cards.groupTitle}>{props.togglesIcon && <Icon name={props.togglesIcon} size={20} />}{props.togglesTitle}</h3>}
            <div className={cards.toggleGrid}>
              {props.toggles.map((tg) => (
                <button key={tg.id} className={`${cards.toggle} ${tg.selected ? cards.toggleOn : ''}`} aria-pressed={tg.selected} onClick={() => run(api, tg.intent)}>
                  <Icon name={tg.icon} size={30} />
                  <span>{tg.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </Panel>
  );
}

export const counters = defineWidget({
  key: 'counters',
  schema,
  Component: CountersList,
  variants: [{ id: 'cards', label: 'widget.variant.cards', Component: CountersCards }],
});
